import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * rr.response.data is a raw server envelope (and rr.message may be
 * undefined), so state.error had an unstable object/string/undefined shape
 * that consumers could not render. Reduce to a short, serializable message.
 */
const toSafeError = (err: any): string => {
  const payload = err?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof err?.message === 'string' ? err.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
};
import { financialStatementsApi } from '@/services/accountingService';

interface FinancialStatementsState {
  financialStatements: any[];
  incomeStatementGL: any | null;
  balanceSheetGL: any | null;
  retainedEarningsConfig: any | null;
  loading: boolean;
  error: any | null;
}

const initialState: FinancialStatementsState = {
  financialStatements: [],
  incomeStatementGL: null,
  balanceSheetGL: null,
  retainedEarningsConfig: null,
  loading: false,
  error: null,
};

export const fetchFinancialStatements = createAsyncThunk(
  'financialStatements/fetchFinancialStatements',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await financialStatementsApi.getAll(params || {});
      return response.data.data || response.data;
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const getRetainedEarningsConfig = createAsyncThunk(
  'financialStatements/getRetainedEarningsConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await financialStatementsApi.getRetainedEarningsConfig();
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const updateRetainedEarningsConfig = createAsyncThunk(
  'financialStatements/updateRetainedEarningsConfig',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response: any = await financialStatementsApi.updateRetainedEarningsConfig(data);
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

const financialStatementsSlice = createSlice({
  name: 'financialStatements',
  initialState,
  reducers: {
    resetFinancialStatements(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: FinancialStatementsState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: FinancialStatementsState, action: any) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchFinancialStatements.pending, handlePending)
      .addCase(fetchFinancialStatements.fulfilled, (state, action) => {
        state.loading = false;
        state.financialStatements = action.payload;
      })
      .addCase(fetchFinancialStatements.rejected, handleRejected)
      
      .addCase(getRetainedEarningsConfig.pending, handlePending)
      .addCase(getRetainedEarningsConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.retainedEarningsConfig = action.payload ?? null;
      })
      .addCase(getRetainedEarningsConfig.rejected, handleRejected)

      .addCase(updateRetainedEarningsConfig.pending, handlePending)
      .addCase(updateRetainedEarningsConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.retainedEarningsConfig = action.payload ?? null;
      })
      .addCase(updateRetainedEarningsConfig.rejected, handleRejected);
  },
});

export const { resetFinancialStatements } = financialStatementsSlice.actions;

export const selectFinancialStatements = (state: any) => state.financialStatements.financialStatements;
export const selectIncomeStatementGL = (state: any) => state.financialStatements.incomeStatementGL;
export const selectBalanceSheetGL = (state: any) => state.financialStatements.balanceSheetGL;
export const selectRetainedEarningsConfig = (state: any) => state.financialStatements.retainedEarningsConfig;
export const selectFinancialStatementsLoading = (state: any) => state.financialStatements.loading;
export const selectFinancialStatementsError = (state: any) => state.financialStatements.error;

export default financialStatementsSlice.reducer;
